import { ThresholdBar } from '../../../core/components/threshold-bar/ThresholdBar.js';
import { isInitialZoom } from '../../../core/components/zoom/utils/is-initial-zoom.js';
import { DateThresholdPosition } from '../../../core/types/thresholds.js';
import { transformThresholdsToMarkers } from '../../../core/utils/thresholds-to-markers.js';
import {
  useCurrentDomainState,
  useSetState,
} from '../../hooks/store/selectors.js';
import { useThresholdConfig } from '../../hooks/useThresholdConfig.js';
import { useTimeseriesYFormatter } from '../../hooks/useTimeseriesYFormatter.js';
import { useTimeseriesZoomRanges } from '../../hooks/useTimeseriesZoomRanges.js';
import { useYScales } from '../../hooks/useYScales.js';

interface TimeseriesThresholdBarProps {
  position: DateThresholdPosition;
  length: number;
}

export function TimeseriesThresholdBar(props: TimeseriesThresholdBarProps) {
  const { position, length } = props;

  const { left: leftThreshold, right: rightThreshold } = useThresholdConfig();
  const { left, right } = useYScales();
  const currentDomain = useCurrentDomainState();
  const { initialZoom } = useTimeseriesZoomRanges();
  const isChartWithoutZoom =
    !currentDomain || isInitialZoom(currentDomain, initialZoom as [Date, Date]);
  const [leftFormatter, rightFormatter] = useTimeseriesYFormatter();
  const setState = useSetState();

  const isFlipped = position !== 'left';

  const yScale = position === 'left' ? left : right;
  if (!yScale) {
    return null;
  }

  const thresholds = position === 'left' ? leftThreshold : rightThreshold;
  if (!thresholds || !thresholds.length) {
    return null;
  }

  const axisFormatter = position === 'left' ? leftFormatter : rightFormatter;

  const markers = transformThresholdsToMarkers(
    thresholds,
    position,
    yScale.scale,
    axisFormatter,
  );

  const handleHover = (id: string | undefined) => {
    setState((prev) => ({
      ...prev,
      thresholds: {
        ...thresholds,
        hoveredThresholdId: id,
      },
    }));
  };

  return markers.length ? (
    <ThresholdBar
      markers={isChartWithoutZoom ? markers : []}
      length={length}
      layout="vertical"
      isFlipped={isFlipped}
      onHover={handleHover}
    />
  ) : null;
}

TimeseriesThresholdBar['displayName'] = 'TimeseriesThresholdBar';
